import { BudgetComponentDto } from 'api/budgets/models/BudgetComponentDto';
import PageBreadcrumbsPortal from 'common/components/pageBreadcrumbsPortal/PageBreadcrumbsPortal';
import PageContainer from 'common/components/pageContainer/PageContainer';
import PageHeader from 'common/components/pageHeader/PageHeader';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import Logger from 'common/services/Logger';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_DECIMAL_PLACES, LOGGER_LOG_TYPE } from 'Config';
import BudgetsService from 'api/budgets/BudgetsService';
import { useNavigate, useParams } from 'react-router-dom';
import Loading from 'common/services/Loading';
import { FormProvider, useForm } from 'react-hook-form';
import { Tab, Tabs } from 'react-bootstrap';
import Button from 'common/components/button/Button';
import ConfirmDeleteModal from 'common/components/modal/confirmDeleteModal/ConfirmDeleteModal';
import { FaTrashAlt } from 'react-icons/fa';
import { Reducers } from 'store/types';
import { useSelector, useDispatch } from 'react-redux';
import UsersService from 'api/users/UsersService';
import { UserProfile } from 'api/account/models/UserProfile';
import Toast from 'common/services/Toast';
import { BudgetLineType } from 'api/budgets/enums/BudgetLineType';
import UnitsService from 'api/units/UnitsService';
import TaxesService from 'api/taxes/TaxesService';
import { UnitDto } from 'api/units/models/UnitDto';
import { TaxDto } from 'api/taxes/models/TaxDto';
import styles from './BudgetComponentScreen.module.scss';
import ColumnsOptionsSidebar, { ColumnsOptionsSidebarColumn } from 'common/components/columnsOptionsSidebar/ColumnsOptionsSidebar';
import { Form } from 'common/components/form/Form';
import { calculateLineTotals, setLines, setOpenLinesIndexes, updateLine } from 'store/budget/action';
import { createLine } from 'store/budget/reducer';
import store from 'store/store';
import InfoMessage from 'common/components/infoMessage/InfoMessage';
import BudgetComponentDetails from './details/BudgetComponentDetails';
import BudgetsCache, { BudgetsCacheData } from 'screens/business/budgets/budget/utils/BudgetsCache';
import BudgetComponentLines from './lines/BudgetComponentLines';
import { LinesTableColumn, mergeWithExistingParameters } from 'screens/business/budgets/budget/lines/utils';
import FamiliesService from 'api/families/FamiliesService';
import { FamilyDto } from 'api/families/models/FamilyDto';
import Utils from 'common/services/Utils';
import { BudgetLineParameterDto } from 'api/budgets/models/BudgetLineParameterDto';
import { ParameterSectionBaseModel, ParameterSectionParameterType } from 'common/components/parametersSections/models';

const PAGE_ID = 'BUDGET_COMPONENT_LINES';
const url = '/business/budgets-components';

type TabKey = 'details' | 'lines' | 'prices';

type FormModel = Omit<BudgetComponentDto, 'lines'>;

function BudgetComponentScreen(): JSX.Element | null {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id, type } = useParams<{ id: string, type: 'create' | 'details' | 'edit' | 'version', version: string }>();
    const form = useForm<FormModel>({ shouldUnregister: false });
    const [loaded, setLoaded] = useState<boolean>(false);
    const currentUrl = url + '/' + type + (id ? ('/' + id) : '');
    const isDetails = type === 'details' || type === 'version';
    const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
    const loggedUser = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const canWrite = type !== 'version' && UsersService.hasPolicies(loggedUser?.policies ?? [], ['BUDGETS_WRITE'])
    const canRead = UsersService.hasPolicies(loggedUser?.policies ?? [], ['BUDGETS_READ'])
    const [currentTabKey, setCurrentTabKey] = useState<TabKey>('details');
    const [taxes, setTaxes] = useState<TaxDto[]>([]);
    const [units, setUnits] = useState<UnitDto[]>([]);
    const [families, setFamilies] = useState<FamilyDto[]>([]);
    const [budgetData, setBudgetData] = useState<BudgetsCacheData>(BudgetsCache.get(PAGE_ID, {
        hiddenColumns: [
            LinesTableColumn.GROSS_UNIT_PRICE,
            LinesTableColumn.MARGIN,
            LinesTableColumn.DISCOUNT,
            LinesTableColumn.UNIT_PRICE_WITH_CHILDREN,
            LinesTableColumn.TOTAL_INCLUDING_TAX,
            LinesTableColumn.TOTAL_UNIT_EXCLUDING_TAX,
        ],
    }));

    const dispatch = useDispatch();

    const linesTableColumns: ColumnsOptionsSidebarColumn<LinesTableColumn>[] = [
        {
            name: t('budgets.budget.lines.header.line_number'),
            field: LinesTableColumn.LINE_NUMBER,
        },
        {
            name: t('budgets.budget.lines.header.quantity'),
            field: LinesTableColumn.QUANTITY,
        },
        {
            name: t('budgets.budget.lines.header.unity'),
            field: LinesTableColumn.UNIT,
        },
        {
            name: t('budgets.budget.lines.header.gross_unit_price'),
            field: LinesTableColumn.GROSS_UNIT_PRICE,
        },
        {
            name: t('budgets.budget.lines.header.margin'),
            field: LinesTableColumn.MARGIN,
        },
        {
            name: t('budgets.budget.lines.header.discount'),
            field: LinesTableColumn.DISCOUNT,
        },
        {
            name: t('budgets.budget.lines.header.price_per_unity'),
            field: LinesTableColumn.UNIT_PRICE,
        },
        {
            name: t('budgets.budget.lines.header.unit_price_with_children'),
            field: LinesTableColumn.UNIT_PRICE_WITH_CHILDREN,
        },
        {
            name: t('budgets.budget.lines.header.total_without_tax'),
            field: LinesTableColumn.TOTAL_EXCLUDING_TAX,
        },
        {
            name: t('budgets.budget.lines.header.total_unit_without_tax'),
            field: LinesTableColumn.TOTAL_UNIT_EXCLUDING_TAX,
        },
        {
            name: t('budgets.budget.lines.header.tax'),
            field: LinesTableColumn.TAX,
        },
        {
            name: t('budgets.budget.lines.header.total_with_tax'),
            field: LinesTableColumn.TOTAL_INCLUDING_TAX,
        },
        {
            name: t('budgets.budget.lines.header.element_image'),
            field: LinesTableColumn.ELEMENT_IMAGE,
        },
    ];

    const getData = async () => {
        try {
            let result: Partial<BudgetComponentDto> = {
                id,
                description: '',
                lines: [],
                decimalPlaces: DEFAULT_DECIMAL_PLACES,
            }

            Loading.show();

            if (id) {
                result = await BudgetsService.getById(id, isDetails);
            }

            const [unitsData, taxesData, familiesData] = await Promise.all([
                UnitsService.getFormattedCatalog(),
                TaxesService.getFormattedCatalog(),
                FamiliesService.getElementsCatalog({ familyIds: [], isElement: false, isComponent: true }),
            ]);

            setUnits(unitsData);
            setTaxes(taxesData);
            setFamilies(familiesData);

            if (!result.lines || result.lines.length === 0) {
                const rootKey = Utils.newGuid();
                const rootSection = {
                    ...createLine(BudgetLineType.SECTION, null, rootKey),
                    isOpen: true,
                };

                result.lines = [
                    rootSection,
                    createLine(BudgetLineType.COMPONENT, rootKey, Utils.newGuid()),
                ];
            }

            // dispatch(setBudget({
            //     defaultTaxId: result.defaultTaxId,
            //     totalNetExcludingTax: result.totalNetExcludingTax ?? 0,
            //     totalTax: result.totalTax ?? 0,
            //     totalIncludingTax: result.totalIncludingTax ?? 0,
            //     totalExcludingTax: result.totalExcludingTax ?? 0,
            //     discount: result.discount ?? 0,
            //     discountTaxId: result.discountTaxId || null,
            //     discountTaxValue: result.discountTaxValue,
            //     defaultTaxValue: result.defaultTaxValue,
            //     decimalPlaces: result.decimalPlaces ?? 0
            // }));
            const openLinesArray = store.getState().budget.openLinesIndexes
            const newLines = result.lines.map((x,i) => {
                return { ...x, isOpen: x.lineType === BudgetLineType.SECTION ? openLinesArray.includes(i) : true }
            })

            dispatch(setOpenLinesIndexes([]))
            dispatch(setLines(newLines));

            result.lines = [];
            form.reset(result);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get budgets', error);
            Toast.error(t('messages.error_load_info'));
        } finally {
            Loading.hide();
            setLoaded(true);
        }
    }

    const onSubmit = async (model: FormModel) => {
        try {
            Loading.show();

            // const tempModel: BudgetComponentDto = {
            //     ...model,
            //     ...store.getState().budget.budget,
            //     lines: store.getState().budget.lines,
            // }

            // const tempArray: number[] = []
            // tempModel.lines.forEach((element,i) => {
            //     if (element.isOpen) {
            //         tempArray.push(i)
            //     }
            // });
            // dispatch(setOpenLinesIndexes(tempArray))

            // if (tempModel && tempModel.id) {
            //     await BudgetsService.update(tempModel)
            //     await getData();
            // } else if (tempModel) {
            //     const id = await BudgetsService.create(tempModel)
            //     navigateTo('edit', id);
            // }
            Toast.success(t('messages.record_save_success'));
        } catch (error: any) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t update save the budget', error);
            Toast.error(t('messages.record_save_error'));
        }
        Loading.hide();
    }

    const onInvalid = () => {
        onSelectTab('details')
        Toast.warning(t('messages.required_fields_empty'));
    }

    const onDelete = async (item: boolean) => {
        if (!item) {
            setShowConfirmDeleteModal(false)
            return;
        }
        try {
            Loading.show();

            await BudgetsService.remove(form.getValues())
            onGoBack();

            Loading.hide();
            Toast.success(t('messages.record_delete_success'));
        } catch (error: any) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t delete budget', error);
            Toast.error(t('messages.record_delete_error'));
        }
        setShowConfirmDeleteModal(false)
    }

    const onGoBack = () => {
        if (type === 'version') {
            navigateTo('details', id);
        } else {
            navigate(url);
        }
    }

    const navigateTo = (type: string, id?: string) => {
        navigate(url + '/' + type + (id ? ('/' + id) : ''));
    }

    const onSelectTab = (k: string | null) => {
        if (!isDetails && (k === 'lines')) {
            const familyId = form.watch('familyId');

            if (!familyId) {
                onInvalid();
                if (!familyId) {
                    form.setError('familyId', { type: 'custom', message: t('common.errors.required') });
                }
                return;
            }
        }

        setCurrentTabKey(k as TabKey)
    }

    const getFamilyParameters = async (familyId: string | null | undefined, subFamilyId: string | null | undefined): Promise<BudgetLineParameterDto[]> => {
        if (!familyId && !subFamilyId) {
            return [];
        }

        const params = await FamiliesService.getFamilyParameters(subFamilyId ?? familyId!)
        if (!params || params.length === 0) {
            return [];
        }

        const removeEmptySections = (nodes: BudgetLineParameterDto[]): BudgetLineParameterDto[] => {
            return nodes
                .map(node => ({
                    ...node,
                    children: removeEmptySections(node.children),
                }))
                .filter(node =>
                    node.parameterType !== ParameterSectionParameterType.TEXT ||
                    (node.children && node.children.length > 0)
                );
        };

        const mapParameter = (p: ParameterSectionBaseModel, i: number) => {
            return {
                id: p.key,
                parentId: p.parentKey,
                key: Utils.newGuid(),
                elementParameterId: undefined,
                elementParameterParentId: null,
                position: i,
                budgetLineParameterElementId: null,
                familyParameterId: p.id,
                children: [],
                parameterName: p.parameterName,
                parameterType: p.parameterType,
                parameterFamilyId: p.parameterFamilyId,
                parameterIsRequired: p.parameterIsRequired,
                visibleOnlyInBudgets: p.visibleOnlyInBudgets,
                type: p.type,
                title: p.title,
                unitId: p.unitId,
                tempId: p.key,
                tempParentId: p.parentKey,
                number: p.parameterType === ParameterSectionParameterType.NUMBER
                    ? {
                        number: null,
                        numberExpression: null,
                        unitId: p.unitId,
                    }
                    : undefined,
                quantity: p.parameterType === ParameterSectionParameterType.QUANTITY
                    ? {
                        quantity: null,
                        quantityExpression: null,
                        unitId: p.unitId,
                    }
                    : undefined,
                text: p.parameterType === ParameterSectionParameterType.TEXT
                    ? {
                        text: null,
                    }
                    : undefined,
            };
        }

        const mappedParams = params.map(mapParameter).filter(p =>
            p.parameterType !== ParameterSectionParameterType.PRODUCTS &&
            p.parameterType !== ParameterSectionParameterType.IMAGES &&
            p.parameterType !== ParameterSectionParameterType.NUMBER_MIN_MAX
        );

        // Group into a tree structure
        const paramMap = new Map<string, BudgetLineParameterDto>();
        const roots: BudgetLineParameterDto[] = [];

        mappedParams.forEach(param => {
            paramMap.set(param.tempId, param);
        });

        mappedParams.forEach(param => {
            if (param.tempParentId && paramMap.has(param.tempParentId)) {
                const parent = paramMap.get(param.tempParentId)!;
                parent.children.push(param);
            } else {
                roots.push(param);
            }
        });

        return removeEmptySections(roots);
    }

    const onChangeFamily = async () => {
        const params = await getFamilyParameters(form.getValues('familyId'), form.getValues('subFamilyId'));
        const componentKey = store.getState().budget.lines.find(x => x.lineType === BudgetLineType.COMPONENT)?.key;
        if (!componentKey) {
            return;
        }

        dispatch(updateLine({
            key: componentKey,
            parameters: mergeWithExistingParameters(params ?? [], [])
        }));

        dispatch(calculateLineTotals(componentKey));
    }

    useEffect(() => {
        void getData();
    }, [id, type]);

    useEffect(() => {
        BudgetsCache.save(PAGE_ID, budgetData);
    }, [budgetData]);

    if (!canRead) {
        return null;
    }

    const title = (type === 'create' ? t('common.new') : (type === 'edit' ? t('common.edit') : t('common.details'))) + ' ' + t('budgets.budget.component');

    return (
        <ScreenTitle title={title}>
            <PageBreadcrumbsPortal
                breadcrumbs={[
                    { name: t('home.title'), url: '/' },
                    { name: t('budgets.list.components_title'), url },
                    { name: title, url: currentUrl },
                ]}
            />
            <PageHeader title={title} informationText={t('common.go_back')} showGoBack addSidebarSpacing={currentTabKey === 'lines'} onGoBack={onGoBack}>
                {canWrite && !isDetails && <Button form='formBudget' type='submit'>{t('common.save')}</Button>}
                {canWrite && isDetails && <Button fw onClick={() => { navigateTo('edit', id); }}>{t('common.edit')}</Button>}
                {canWrite && isDetails && (
                    <Button variant='danger' onClick={() => setShowConfirmDeleteModal(true)}>
                        <FaTrashAlt />
                    </Button>
                )}
            </PageHeader>
            <PageContainer>
                {type === 'version' && <div className={styles.versionWarning}>
                    <InfoMessage message={t('budgets.budget.version_warning')} type='information'/>
                </div>}
                <FormProvider {...form}>
                    <Form id='formBudget' handleSubmit={form.handleSubmit} onSubmit={onSubmit} onInvalid={onInvalid} noValidate className={styles.form}>
                        {loaded && <Tabs activeKey={currentTabKey} onSelect={(k) => onSelectTab(k)}>
                            <Tab eventKey={'details'} title={t('budgets.budget.tab_details')}>
                                <BudgetComponentDetails
                                    type={type}
                                    families={families}
                                    onChangeFamily={onChangeFamily}
                                    onChangeSubFamily={onChangeFamily}
                                />
                            </Tab>
                            <Tab eventKey={'lines'} title={t('budgets.budget.tab_lines')}>
                                <BudgetComponentLines
                                    type={type}
                                    taxes={taxes}
                                    units={units}
                                    budgetData={budgetData}
                                />
                            </Tab>
                        </Tabs>}
                    </Form>
                </FormProvider>
            </PageContainer>
            {currentTabKey === 'lines' && (
                <ColumnsOptionsSidebar
                    columns={linesTableColumns}
                    hiddenColumns={budgetData.hiddenColumns}
                    canDrag={false}
                    onChangeHiddenColumns={(hc) => setBudgetData(d => ({ ...d, hiddenColumns: hc }))}
                    onChangeColumnsOrder={(oc) => setBudgetData(d => ({ ...d, columnsOrder: oc }))}
                />
            )}
            <ConfirmDeleteModal
                itemName={form.getValues('description')}
                isOpen={showConfirmDeleteModal}
                onClose={onDelete}
            />

        </ScreenTitle>
    );
}

export default BudgetComponentScreen;
